import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import UsageIndicator from '../../components/UsageIndicator';
import ModeSelector from '../../components/ModeSelector';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import DeepResearchProgress from '../../components/DeepResearchProgress';
import { FileText, Target, Layout } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../lib/firebase';
import { authenticatedApiCall } from '../../lib/apiClient';
import { useUser } from '@/context/UserContext';
import { emitUsageRefresh } from '@/lib/usageEvents';

const BriefGenerator = () => {
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [goal, setGoal] = useState('');
  const [format, setFormat] = useState('Blog Post');
  const [result, setResult] = useState('');
  const [sources, setSources] = useState([]); // ✅ ADDED
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('basic');

  const { plan, loading: userLoading, authReady } = useUser();

  if (userLoading || !authReady) {
    return <ToolLayout title="Content Brief Generator">Loading…</ToolLayout>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      toast.error('Please login');
      return;
    }

    setLoading(true);
    setResult('');
    setSources([]);

    try {
      const data = await authenticatedApiCall('/api/generate-brief', {
        topic,
        audience,
        goal,
        format,
        mode,
      });

      setResult(data.brief);
      setSources(data.sources || []);

      emitUsageRefresh({ tool: 'brief', mode });
      toast.success('Brief generated');
    } catch (err) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolLayout title="Content Brief Generator">
      <div className="max-w-4xl mx-auto">
        <UsageIndicator />

        <ModeSelector
          mode={mode}
          setMode={setMode}
          subscription={{ plan, status: 'active' }}
        />

        {mode === 'deep' && loading && (
          <DeepResearchProgress isActive={loading} />
        )}

        <form onSubmit={handleSubmit} className="space-y-6 mb-8">
          <Input label="Topic" icon={FileText} value={topic} onChange={setTopic} />
          <Input
            label="Target Audience"
            icon={Target}
            value={audience}
            onChange={setAudience}
          />
          <Input label="Content Goal" value={goal} onChange={setGoal} />

          <div>
            <label className="text-white font-medium mb-2 flex gap-2">
              <Layout size={18} /> Content Format
            </label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-neutral-900 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-accent-teal"
            >
              <option>Blog Post</option>
              <option>Landing Page</option>
              <option>Email Sequence</option>
              <option>Social Media Content</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-accent-teal text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {loading ? 'Generating…' : 'Generate Brief'}
          </button>
        </form>

        {result && (
          <div className="bg-white/5 p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-bold mb-4 text-white">
              Generated Content Brief
            </h2>

            <MarkdownRenderer content={result} />

            {mode === 'deep' && sources.length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <h3 className="text-sm font-semibold text-white mb-2">
                  Sources
                </h3>
                <ul className="space-y-1 text-sm">
                  {sources.map((s, i) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-teal underline"
                      >
                        {s.title || s.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

const Input = ({ label, value, onChange, icon: Icon }) => (
  <div>
    <label className="text-white font-medium mb-2 flex gap-2">
      {Icon && <Icon size={18} />} {label}
    </label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-lg bg-white/5 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-accent-teal"
      required
    />
  </div>
);

export default BriefGenerator;
